import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import type { TableProps } from "antd";
import {
  useCallback,
  useMemo,
  type ComponentType,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import {
  CrossIcon,
  DeleteIcon,
  EditIcon,
  SearchIcon,
} from "src/assets/svgs";
import {
  CustomButton,
  CustomTable,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";
import airplaneIcon from "src/assets/icons/vehicleTypes/airplane.svg?url";
import ambulanceIcon from "src/assets/icons/vehicleTypes/ambulance.svg?url";
import bikeIcon from "src/assets/icons/vehicleTypes/bike.svg?url";
import boatIcon from "src/assets/icons/vehicleTypes/boat.svg?url";
import busIcon from "src/assets/icons/vehicleTypes/bus.svg?url";
import carIcon from "src/assets/icons/vehicleTypes/car.svg?url";
import cementIcon from "src/assets/icons/vehicleTypes/cement.svg?url";
import craneIcon from "src/assets/icons/vehicleTypes/crane.svg?url";
import firetruckIcon from "src/assets/icons/vehicleTypes/firetruck.svg?url";
import forkliftIcon from "src/assets/icons/vehicleTypes/forklift.svg?url";
import garbageTruckIcon from "src/assets/icons/vehicleTypes/garbage-truck.png?url";
import helicopterIcon from "src/assets/icons/vehicleTypes/helicopter.svg?url";
import minivanIcon from "src/assets/icons/vehicleTypes/minivan.svg?url";
import motorcycleIcon from "src/assets/icons/vehicleTypes/motorcycle.svg?url";
import pickupIcon from "src/assets/icons/vehicleTypes/pickup.svg?url";
import roadrollerIcon from "src/assets/icons/vehicleTypes/roadroller.svg?url";
import scooterIcon from "src/assets/icons/vehicleTypes/scooter.svg?url";
import snowplowIcon from "src/assets/icons/vehicleTypes/snowplow.svg?url";
import subwayIcon from "src/assets/icons/vehicleTypes/subway.svg?url";
import tankerIcon from "src/assets/icons/vehicleTypes/tanker.svg?url";
import tractorIcon from "src/assets/icons/vehicleTypes/tractor.svg?url";
import trailerIcon from "src/assets/icons/vehicleTypes/trailer.svg?url";
import trainIcon from "src/assets/icons/vehicleTypes/train.svg?url";
import truckIcon from "src/assets/icons/vehicleTypes/truck.svg?url";
import vanIcon from "src/assets/icons/vehicleTypes/van.svg?url";
import CustomSharedInput from "sipli-fleet-core/components/CustomSharedInput/CustomSharedInput";
import type { VehicleTypeRecord } from "src/services/administrator/vehicleTypeService";
import { hasAdministrationPermission } from "src/util/administrationPermission";

interface VehicleTypesTableProps {
  data: VehicleTypeRecord[];
  isLoading?: boolean;
  pagination: { page: number; size: number; total?: number };
  setPagination: Dispatch<SetStateAction<{ page: number; size: number }>>;
  sorting: { sortBy: string | undefined; sortDir: "ASC" | "DESC" | undefined };
  setSorting: Dispatch<
    SetStateAction<{ sortBy: string | undefined; sortDir: "ASC" | "DESC" | undefined }>
  >;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  onEdit: (record: VehicleTypeRecord) => void;
}

const CrossIconComponent = CrossIcon as unknown as ComponentType<{ className?: string }>;
const DeleteIconComponent = DeleteIcon as unknown as ComponentType<{ className?: string }>;
const EditIconComponent = EditIcon as unknown as ComponentType<{ className?: string }>;
const SearchIconComponent = SearchIcon as unknown as ComponentType<{ className?: string }>;
const { CustomText } = CustomTypography;

const dataIndexToColumnId: Record<string, string> = {
  name: "NAME",
  status: "STATUS",
};

const iconMap: Record<string, string> = {
  airplane: airplaneIcon,
  ambulance: ambulanceIcon,
  bike: bikeIcon,
  boat: boatIcon,
  bus: busIcon,
  car: carIcon,
  cement: cementIcon,
  crane: craneIcon,
  firetruck: firetruckIcon,
  forklift: forkliftIcon,
  garbage_truck: garbageTruckIcon,
  helicopter: helicopterIcon,
  minivan: minivanIcon,
  motorcycle: motorcycleIcon,
  pickup: pickupIcon,
  roadroller: roadrollerIcon,
  scooter: scooterIcon,
  snowplow: snowplowIcon,
  subway: subwayIcon,
  tanker: tankerIcon,
  tractor: tractorIcon,
  trailer: trailerIcon,
  train: trainIcon,
  truck: truckIcon,
  van: vanIcon,
};

const isActive = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return ["active", "true", "1"].includes(value.toLowerCase());
  return false;
};

const StatusBadge = ({ active }: { active?: unknown }) => {
  const { t } = useTranslation();
  const activeStatus = isActive(active);

  return (
    <CustomTag
      className={
        activeStatus
          ? "bg-green-50 text-green-600"
          : "bg-gray-50 text-gray-400"
      }
    >
      {activeStatus ? t("active") : t("inactive")}
    </CustomTag>
  );
};

const VehicleTypeIconCell = ({ record }: { record: VehicleTypeRecord }) => {
  const name = typeof record.name === "string" ? record.name : "";
  const iconSrc = iconMap[name];

  if (iconSrc) {
    return (
      <img
        src={iconSrc}
        width={40}
        height={40}
        alt={name}
        className="h-10 w-10 object-contain"
      />
    );
  }

  return (
    <CustomText className="!text-sm !text-black">
      {typeof record.description === "string" && record.description.trim()
        ? record.description
        : "No Icon"}
    </CustomText>
  );
};

const VehicleTypesTable = ({
  data,
  isLoading = false,
  pagination,
  setPagination,
  sorting,
  setSorting,
  search,
  setSearch,
  onEdit,
}: VehicleTypesTableProps) => {
  const { t } = useTranslation();
  const canEdit = hasAdministrationPermission("edit");
  const canDelete = hasAdministrationPermission("delete");

  const onTableChange: TableProps<VehicleTypeRecord>["onChange"] = (
    _pagination,
    _filters,
    sorter,
    extra,
  ) => {
    if (extra.action === "filter") return;
    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const order = normalizedSorter?.order;
    const dataIndex = normalizedSorter?.field || normalizedSorter?.columnKey;
    if (!order || !dataIndex) {
      setSorting({ sortBy: "ID", sortDir: "DESC" });
      return;
    }
    setSorting({
      sortBy: dataIndexToColumnId[String(dataIndex)] ?? "ID",
      sortDir: order === "ascend" ? "ASC" : "DESC",
    });
    setPagination((previous) => ({ ...previous, page: 0 }));
  };

  const renderActions = useCallback(
    (record: VehicleTypeRecord) => {
      const disabledActionClass =
        "!cursor-not-allowed !opacity-90 hover:!bg-transparent active:!bg-transparent focus:!bg-transparent !bg-transparent";
      const editDisabled = true;
      const deleteDisabled = true;

      return (
        <div className="flex items-center justify-center gap-2 customer-actions">
          {canEdit && (
            <CustomTooltip title={t("edit")}>
              <span className="inline-flex">
                <CustomButton
                  type="icon"
                  buttonSize="small"
                  aria-label={t("edit_vehicle_type")}
                  disabled={editDisabled}
                  className={disabledActionClass}
                  icon={<EditIconComponent className="w-[16px] h-[16px] opacity-30 grayscale" />}
                  onClick={(event) => {
                    event?.stopPropagation?.();
                    onEdit(record);
                  }}
                />
              </span>
            </CustomTooltip>
          )}
          {canDelete && (
            <CustomTooltip title={t("delete")}>
              <span className="inline-flex">
                <CustomButton
                  type="icon"
                  buttonSize="small"
                  aria-label={t("delete_vehicle_type")}
                  disabled={deleteDisabled}
                  className={disabledActionClass}
                  icon={<DeleteIconComponent className="w-[15px] h-[16px] opacity-30 grayscale" />}
                />
              </span>
            </CustomTooltip>
          )}
          {!canEdit && !canDelete && (
            <CustomTooltip title={t("permission_denied")}>
              <span className="inline-flex">
                <CustomButton
                  type="icon"
                  buttonSize="small"
                  aria-label={t("edit_vehicle_type")}
                  disabled
                  className={disabledActionClass}
                  icon={<EditIconComponent className="w-[16px] h-[16px] opacity-30 grayscale" />}
                />
              </span>
            </CustomTooltip>
          )}
        </div>
      );
    },
    [canDelete, canEdit, onEdit, t],
  );

  const columns: TableProps<VehicleTypeRecord>["columns"] = useMemo(
    () => [
      {
        title: t("vehicle_type"),
        dataIndex: "name",
        key: "name",
        sorter: true,
        render: (value: string) => (
          <CustomText className="!text-sm !font-semibold !text-black">{value || "--"}</CustomText>
        ),
      },
      {
        title: t("icon"),
        dataIndex: "icon",
        key: "icon",
        render: (_value, record) => <VehicleTypeIconCell record={record} />,
      },
      {
        title: t("status"),
        dataIndex: "status",
        key: "status",
        sorter: true,
        render: (_value, record) => <StatusBadge active={record.active} />,
      },
      {
        title: t("actions"),
        key: "actions",
        align: "center",
        width: 120,
        render: (_value, record) => renderActions(record),
      },
    ],
    [renderActions, t],
  );

  return (
    <>
      <div className="flex items-center justify-between gap-3 px-[18px] -mt-2 mb-4">
        <CustomSharedInput
          allowClear={{
            clearIcon: (
              <CrossIconComponent className="mr-0.5 customers-search-clear-icon w-[8.5px] h-[8.5px]" />
            ),
          }}
          prefix={<SearchIconComponent className="customers-search-icon w-[14px] h-[14px]" />}
          placeholder={t("search")}
          value={search}
          className="global-filter-search-class !rounded-[4px]"
          onChange={(event) => {
            setSearch(event.target.value);
            setPagination((previous) => ({ ...previous, page: 0 }));
          }}
        />
      </div>

      <CustomTable<VehicleTypeRecord>
        customClass="customers-table"
        data={data}
        columns={columns}
        rowKey={(record) => String(record.id)}
        isLoading={isLoading}
        pageSize={pagination.size}
        currentPage={pagination.page}
        totalCount={pagination.total ?? data.length}
        handlePagination={(page) => setPagination((previous) => ({ ...previous, page }))}
        handlePageSizeChange={(size) => setPagination({ page: 0, size: Number(size) })}
        showSearchInput={false}
        showCustomizeColumns={false}
        showDownload={false}
        sorting={sorting}
        onChange={onTableChange}
        locale={{ emptyText: t("no_data") }}
      />
    </>
  );
};

export default VehicleTypesTable;
